import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Lock, Eye, Database, Mail, ArrowLeft, BrainCircuit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-b border-gray-200 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 hover:scale-105 transition-all duration-300 cursor-pointer group">
            <img 
              src="/Favicon/favicon-196x196.png" 
              alt="KaufPause Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10 -translate-y-0.5"
            />
            <span className="text-2xl font-bold text-gray-900">
              Kauf<span className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-700 bg-clip-text text-transparent group-hover:from-emerald-400 group-hover:via-emerald-500 group-hover:to-emerald-600 transition-all duration-300">Pause</span>
            </span>
          </div>
          <button 
            onClick={() => navigate('/')} 
            className="text-gray-600 hover:text-emerald-600 transition-colors"
          >
            ← Zurück zur Startseite
          </button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-16 mt-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Shield className="w-8 h-8 text-emerald-600" />
              <h1 className="text-4xl font-bold text-gray-900">Datenschutzerklärung</h1>
            </div>
            <p className="text-xl text-gray-600">
              Transparenz über den Umgang mit deinen Daten
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Stand: {new Date().toLocaleDateString('de-DE')}
            </p>
          </div>

          <div className="space-y-8">
            <Card className="border-emerald-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-800">
                  <Eye className="w-5 h-5" />
                  1. Überblick
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Diese Datenschutzerklärung informiert Sie über die Art, den Umfang und den Zweck der 
                  Verarbeitung personenbezogener Daten durch KaufPause.
                </p>
                <div className="bg-emerald-50 p-4 rounded-lg border-l-4 border-emerald-600">
                  <p className="text-emerald-800 font-medium">
                    <strong>Kurz gesagt:</strong> Wir sammeln nur die Daten, die für den Betrieb der App 
                    notwendig sind, und geben sie niemals an Dritte weiter.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Database className="w-5 h-5" />
                  2. Welche Daten wir sammeln
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Account-Daten:</h4>
                    <ul className="space-y-1 text-gray-700">
                      <li>• E-Mail-Adresse</li>
                      <li>• Verschlüsseltes Passwort</li>
                      <li>• Account-Erstellungsdatum</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">App-Daten:</h4>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Deine eingetragenen Produkte</li>
                      <li>• Kaufentscheidungen</li>
                      <li>• Ersparnis-Statistiken</li>
                      <li>• Impuls-Check Antworten</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-blue-800">
                    <strong>Wichtig:</strong> Wir sammeln keine Zahlungsdaten, Tracking-Cookies oder 
                    persönliche Informationen außerhalb der App-Funktionalität.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <Lock className="w-5 h-5" />
                  3. Wie wir deine Daten schützen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Technische Sicherheit:</h4>
                    <ul className="space-y-1 text-gray-700">
                      <li>• SSL-Verschlüsselung</li>
                      <li>• EU Datenbank Server</li>
                      <li>• Regelmäßige Sicherheitsupdates</li>
                      <li>• Passwort-Hashing</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Organisatorisch:</h4>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Minimaler Datenzugriff</li>
                      <li>• Keine Weitergabe an Dritte</li>
                      <li>• DSGVO-konforme Verarbeitung</li>
                      <li>• Regelmäßige Löschung</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-800">
                  <Eye className="w-5 h-5" />
                  3.5. YouTube-Videos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Wir binden Tutorial- und Informationsvideos von YouTube ein, um Ihnen die 
                  Nutzung von KaufPause zu erklären.
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Datenschutzfreundliche Einbindung:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Videos werden über youtube-nocookie.com eingebunden</li>
                      <li>• Keine automatische Datenübertragung an Google</li>
                      <li>• Cookies werden erst beim Abspielen gesetzt</li>
                      <li>• Keine Tracking-Cookies im Ruhezustand</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Beim Abspielen:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Verbindung zu YouTube-Servern wird hergestellt</li>
                      <li>• IP-Adresse wird an Google übertragen</li>
                      <li>• YouTube-Datenschutzrichtlinien gelten</li>
                      <li>• Nutzung ist freiwillig</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-600">
                  <p className="text-red-800 font-medium">
                    <strong>Hinweis:</strong> Das Abspielen von YouTube-Videos ist optional. 
                    Sie können KaufPause vollständig nutzen, ohne Videos anzusehen. 
                    Weitere Informationen finden Sie in der 
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" 
                       className="underline ml-1">YouTube-Datenschutzerklärung</a>.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <BrainCircuit className="w-5 h-5" />
                  3.6. KI-Beratung (OpenAI)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Unsere KI-Beratungsfunktion im Impuls-Check nutzt OpenAI's GPT-4 API, um Ihnen 
                  personalisierte Kaufberatung zu bieten.
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Übertragene Daten:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Produktname und -preis</li>
                      <li>• Produktkategorie</li>
                      <li>• Ihre Antworten im Impuls-Check</li>
                      <li>• Keine persönlichen Identifikatoren</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Datenschutz:</h4>
                    <ul className="space-y-1 text-gray-700 text-sm">
                      <li>• Verschlüsselte Datenübertragung</li>
                      <li>• Keine Speicherung von Namen oder E-Mail-Adressen</li>
                      <li>• Ratenlimit: 10 Anfragen pro Stunde</li>
                      <li>• Freiwillige Nutzung - jederzeit deaktivierbar</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-600">
                  <p className="text-amber-800 font-medium">
                    <strong>Wichtig:</strong> Die KI-Beratung ist optional. Sie können KaufPause 
                    vollständig nutzen, ohne diese Funktion zu verwenden. OpenAI verarbeitet die 
                    übertragenen Daten gemäß ihrer eigenen Datenschutzrichtlinien.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-800">
                  <Mail className="w-5 h-5" />
                  4. Deine Rechte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700">
                  Du hast jederzeit das Recht auf:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>Auskunft</strong> über deine gespeicherten Daten</li>
                    <li>• <strong>Berichtigung</strong> falscher Daten</li>
                    <li>• <strong>Löschung</strong> deines Accounts</li>
                  </ul>
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>Datenübertragbarkeit</strong> (Export)</li>
                    <li>• <strong>Widerspruch</strong> gegen Verarbeitung</li>
                    <li>• <strong>Beschwerde</strong> bei Aufsichtsbehörde</li>
                  </ul>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-orange-800">
                    <strong>Kontakt:</strong> Für alle Anfragen zu deinen Daten schreibe einfach an 
                    <a href="mailto:kontakt@kaufpause.at" className="underline ml-1">kontakt@kaufpause.at</a>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-gray-800">5. Weitere Informationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Cookies:</h4>
                    <p className="text-gray-700 text-sm">
                      Wir verwenden technisch notwendige Cookies für die Anmeldung und 
                      Session-Cookies zur temporären Speicherung von Eingaben (z.B. beim 
                      Verfassen von Blog-Artikeln). Keine Tracking- oder Werbe-Cookies.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Externe Dienste:</h4>
                    <p className="text-gray-700 text-sm">
                      Wir nutzen Supabase (EU-Server) für die Datenspeicherung, 
                      PayPal für die sichere Zahlungsabwicklung und YouTube 
                      (über youtube-nocookie.com) für Tutorial-Videos ohne Tracking.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Hosting:</h4>
                    <p className="text-gray-700 text-sm">
                      Unsere Website wird von Helloly gehostet. Die Server befinden sich 
                      in Österreich und unterliegen österreichischem Datenschutzrecht.
                    </p>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 text-sm">
                    <strong>Verantwortlicher:</strong> Tobias Poczesniok, KaufPause<br/>
                    <strong>Kontakt:</strong> kontakt@kaufpause.at<br/>
                    <strong>Hosting:</strong> Helloly (Server in Österreich)<br/>
                    <strong>Letzte Aktualisierung:</strong> {new Date().toLocaleDateString('de-DE')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 